// import redux
import { Action, Dispatch } from 'redux';
// import services
import { StringService } from '@app/services';
// import action type enumerations
import ActionType from '@app/redux/actions/types';
import { NotificationType } from '@app/enumerations';
// import models
import {
    MinCostPerSquareFootVersionRequest,
    MinCostPerSquareFootVersionResponse,
} from '@app/models/api';
// import controller factory
import ControllerFactoryProvider from '@app/controllers/factory';
// import action methods
import {
    addNotification,
    endProcessing,
    startProcessing,
} from '@app/redux/actions/methods';
// import action parameters
import { OverlayTargetParameters } from './ActionParametersBarrel';

interface MinCostPerSquareFootVersionAction extends Action {
    versionId: number;
}

interface MinCostPerSquareFootVersionParameters {
    versionId: number;
}

//#region Load Min Cost Per Square Foot Versions Actions
interface LoadMinCostPerSquareFootVersionsAction extends Action { 
    minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[];
}

const loadMinCostPerSquareFootVersionsAction: (minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[], params: OverlayTargetParameters) =>
    LoadMinCostPerSquareFootVersionsAction = (minCostPerSquareFootVersions: MinCostPerSquareFootVersionResponse[], params: OverlayTargetParameters) => ({
    ...params,
    minCostPerSquareFootVersions,
    type: ActionType.LOAD_MINIMUM_COST_PER_SQUARE_FOOT_VERSIONS,
});

const loadMinCostPerSquareFootVersions = 
    (params: OverlayTargetParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootVersionResponse[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-loadMinCostPerSquareFootVersions`;
        try {
            await startProcessing({
                message: 'Loading min cost per square foot versions...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

        const minCostPerSquareFootVersions = await ControllerFactoryProvider
            .getControllerFactory()
            .getMinCostPerSquareFootVersionController()
            .getAllMinCostPerSquareFootVersions();
        // load to redux
        dispatch<LoadMinCostPerSquareFootVersionsAction>(loadMinCostPerSquareFootVersionsAction(minCostPerSquareFootVersions, params));
        
        await endProcessing({
            messageId,
            overlayTarget: params.overlayTarget,
        })(dispatch);
        return minCostPerSquareFootVersions;
    }
    catch (error: any) {
        await addNotification({
            title: 'Error',
            primaryMessage: error.message,
            secondaryMessage: error.systemMessage,
            notificationType: NotificationType.ERROR,
        })(dispatch);

        await endProcessing({
            messageId,
            overlayTarget: params.overlayTarget,
        })(dispatch);

        throw error;
    }
};

//#endregion Load Min Cost Per Square Foot Versions Actions

//#region Select Min Cost Per Square Foot Version Actions

type SelectMinCostPerSquareFootVersionParameters = MinCostPerSquareFootVersionParameters;

type SelectMinCostPerSquareFootVersionAction = MinCostPerSquareFootVersionAction;

const selectMinCostPerSquareFootVersionAction: (params: SelectMinCostPerSquareFootVersionParameters) =>
    SelectMinCostPerSquareFootVersionAction = (params: SelectMinCostPerSquareFootVersionParameters) => ({
    ...params,
    type: ActionType.SELECT_MINIMUM_COST_PER_SQUARE_FOOT_VERSION,
});

const selectMinCostPerSquareFootVersion = (params: SelectMinCostPerSquareFootVersionParameters) =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // select on redux
            dispatch<SelectMinCostPerSquareFootVersionAction>(selectMinCostPerSquareFootVersionAction(params));
        } 
        catch (error: any) {
            throw error;
        }
    };

//#endregion Select Min Cost Per Square Foot Version Actions

//#region Unselect Min Cost Per Square Foot Version Actions

type UnselectMinCostPerSquareFootVersionAction = Action;

const unselectMinCostPerSquareFootVersionAction: () =>
    UnselectMinCostPerSquareFootVersionAction = () => ({
    type: ActionType.UNSELECT_MINIMUM_COST_PER_SQUARE_FOOT_VERSION,
});

const unselectMinCostPerSquareFootVersion = () =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // unselect on redux
            dispatch<UnselectMinCostPerSquareFootVersionAction>(unselectMinCostPerSquareFootVersionAction());
        } 
        catch (error: any) {
            throw error;
        }
    };

//#endregion Unselect Min Cost Per Square Foot Version Actions

//#region Delete Min Cost Per Square Foot Version Actions

type DeleteMinCostPerSquareFootVersionParameters = OverlayTargetParameters & MinCostPerSquareFootVersionParameters;

const deleteMinCostPerSquareFootVersion = (params: DeleteMinCostPerSquareFootVersionParameters) => 
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootVersionResponse[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-deleteMinCostPerSquareFootVersion`;
        try {
            await startProcessing({
                message: 'Removing Version entry and associated entries in Min Cost Per Square Foot table...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .deleteMinCostPerSquareFootVersion(params.versionId);

            const minCostPerSquareFootVersions = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .getAllMinCostPerSquareFootVersions();
            // load to redux
            dispatch<LoadMinCostPerSquareFootVersionsAction>(loadMinCostPerSquareFootVersionsAction(minCostPerSquareFootVersions, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostPerSquareFootVersions;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
};

//#endregion Delete Min Cost Per Square Foot Version Actions

//#region Add Min Cost Per Square Foot Version Action

interface AddMinCostPerSquareFootVersionParameters extends OverlayTargetParameters {
    minCostPerSquareFootVersionToAdd: MinCostPerSquareFootVersionRequest;
}

const addMinCostPerSquareFootVersion = (params: AddMinCostPerSquareFootVersionParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootVersionResponse[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(
            params
        )}-addMinCostPerSquareFootVersion`;
        try {
            await startProcessing({
                message: 'Saving Min Cost Per Square Foot Version information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .addMinCostPerSquareFootVersion(params.minCostPerSquareFootVersionToAdd);

            const minCostPerSquareFootVersions = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .getAllMinCostPerSquareFootVersions();

            dispatch<LoadMinCostPerSquareFootVersionsAction>(loadMinCostPerSquareFootVersionsAction(minCostPerSquareFootVersions, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostPerSquareFootVersions;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Add Min Cost Per Square Foot Version Action

//#region Update Min Cost Per Square Foot Version Action

interface UpdateMinCostPerSquareFootVersionParameters extends OverlayTargetParameters, MinCostPerSquareFootVersionParameters {
    minCostPerSquareFootVersionToEdit: MinCostPerSquareFootVersionRequest;
}

const updateMinCostPerSquareFootVersion = (params: UpdateMinCostPerSquareFootVersionParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootVersionResponse[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-updateMinCostPerSquareFootVersion`;
        try {
            await startProcessing({
                message: 'Saving Min Cost Per Square Foot Version information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .updateMinCostPerSquareFootVersion(params.versionId, params.minCostPerSquareFootVersionToEdit);

            const minCostPerSquareFootVersions = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .getAllMinCostPerSquareFootVersions();
            // Load to redux.
            dispatch<LoadMinCostPerSquareFootVersionsAction>(loadMinCostPerSquareFootVersionsAction(minCostPerSquareFootVersions, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostPerSquareFootVersions;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Update Min Cost Per Square Foot Version Action

//#region Complete Min Cost Per Square Foot Version Action

type CompleteMinCostPerSquareFootVersionParameters = OverlayTargetParameters & MinCostPerSquareFootVersionParameters;

const completeMinCostPerSquareFootVersion = (params: CompleteMinCostPerSquareFootVersionParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootVersionResponse[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-completeMinCostPerSquareFootVersion`;
        try {
            await startProcessing({
                message: 'Saving Min Cost Per Square Foot Version as completed...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .completeMinCostPerSquareFootVersion(params.versionId);

            const minCostPerSquareFootVersions = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootVersionController()
                .getAllMinCostPerSquareFootVersions();
            // Load to redux
            dispatch<LoadMinCostPerSquareFootVersionsAction>(loadMinCostPerSquareFootVersionsAction(minCostPerSquareFootVersions, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostPerSquareFootVersions;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Complete Min Cost Per Square Foot Version Action

// export actions
export {
    MinCostPerSquareFootVersionAction,
    LoadMinCostPerSquareFootVersionsAction,
    SelectMinCostPerSquareFootVersionAction,
    UnselectMinCostPerSquareFootVersionAction,
};

// export action methods
export {
    addMinCostPerSquareFootVersion,
    completeMinCostPerSquareFootVersion,
    deleteMinCostPerSquareFootVersion,
    loadMinCostPerSquareFootVersions,
    selectMinCostPerSquareFootVersion,
    updateMinCostPerSquareFootVersion,
    unselectMinCostPerSquareFootVersion,
};

// export action parameters
export {
    MinCostPerSquareFootVersionParameters,
    AddMinCostPerSquareFootVersionParameters,
    CompleteMinCostPerSquareFootVersionParameters,
    DeleteMinCostPerSquareFootVersionParameters,
    UpdateMinCostPerSquareFootVersionParameters,
    SelectMinCostPerSquareFootVersionParameters,
};

// export action factories
export {
    loadMinCostPerSquareFootVersionsAction,
    selectMinCostPerSquareFootVersionAction,
    unselectMinCostPerSquareFootVersionAction,
};